// ============================
// src/Pages/Paciente/sesiones.jsx
// ============================

import React, { useEffect, useMemo, useState } from "react";
import { auth } from "../../firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  onSnapshot,
  query,
  where,
  addDoc,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { db } from "../../firebase";
import usePlanesUsuario from "../../hooks/usePlanesUsuario";
import { setLogLevel } from "firebase/firestore";
import { MoreVertical } from "lucide-react";

setLogLevel("debug");

/* ============================
   Helper formato CLP
   ============================ */
const clp = (n) =>
  typeof n === "number" ? `$${n.toLocaleString("es-CL")}` : "—";

// ===============================
// Helpers: WhatsApp + textos motivo
// ===============================
const limpiarWhatsapp = (v) => {
  const raw = (v || "").toString().trim();
  if (!raw) return "";
  let d = raw.replace(/[^\d]/g, "");
  if (d.length === 9 && d.startsWith("9")) d = "56" + d;
  return d;
};

const buildWaLink = (phone, text) => {
  const p = limpiarWhatsapp(phone);
  if (!p) return "";
  return `https://wa.me/${p}?text=${encodeURIComponent(text || "")}`;
};

const motivoReservaTexto = (r) => {
  if (!r) return { titulo: "", lineas: [] };
  const estado = (r.estado || "").toLowerCase();

  if (estado === "rechazada") {
    const motivo = r.rechazoMotivo || r.rechazo_motivo || "";
    return {
      titulo: "Reserva rechazada",
      lineas: motivo ? [`Motivo: ${motivo}`] : ["Motivo: (no especificado)"],
    };
  }

  if (estado === "cancelada") {
    const admin = r.motivo_admin || "";
    const pac = r.motivo_admin_paciente || "";
    const resp = r.motivo_admin_respuesta || "";

    const lineas = [];
    if (admin) lineas.push(`Motivo (admin): ${admin}`);
    if (pac) lineas.push(`Motivo (paciente): ${pac}`);
    if (resp) lineas.push(`Respuesta (kinesióloga): ${resp}`);

    return {
      titulo: "Reserva cancelada",
      lineas: lineas.length ? lineas : ["Motivo: (no especificado)"],
    };
  }

  return { titulo: "", lineas: [] };
};

/* ============================
   Etiquetas de estado
   ============================ */
const Pill = ({ estado }) => {
  const map = {
    pendiente: "bg-amber-100 text-amber-800",
    aprobada: "bg-emerald-100 text-emerald-800",
    rechazada: "bg-rose-100 text-rose-800",
    realizada: "bg-blue-100 text-blue-800",
    cancelada: "bg-gray-200 text-gray-700",
  };
  const label =
    (estado || "").charAt(0).toUpperCase() + (estado || "").slice(1);
  return (
    <span
      className={`px-2 py-1 rounded-full text-xs font-semibold ${
        map[estado] || "bg-gray-100 text-gray-700"
      }`}
    >
      {label || "—"}
    </span>
  );
};

const estados = [
  "todas",
  "pendiente",
  "aprobada",
  "rechazada",
  "realizada",
  "cancelada"
];

/* ============================
   ¿Sesión ya pasó?
   ============================ */
const esPasada = (r) => {
  if (!r.fecha || !r.hora) return false;
  try {
    const [Y, M, D] = r.fecha.split("-").map(Number);
    const [h, m] = r.hora.split(":").map(Number);
    const fecha = new Date(Y, M - 1, D, h, m);
    return new Date() > new Date(fecha.getTime() + 30 * 60000);
  } catch {
    return false;
  }
};

/* ============================
   Nº de sesiones por servicio
   ============================ */
const sesionesPorServicio = (categoria, variante) => {
  const v = (variante || "").toLowerCase();
  const c = (categoria || "").toLowerCase();

  if (v.includes("1 sesión")) return 1;
  if (v.includes("5 sesiones")) return 5;
  if (v.includes("10 sesiones")) return 10;

  if (c.includes("recovery")) {
    if (v.includes("1 sesión")) return 1;
    if (v.includes("5 sesiones")) return 5;
  }

  if (v.includes("mitad")) return 1;

  if (c.includes("entrenamiento")) {
    const semanas = v.includes("mensual")
      ? 4
      : v.includes("trimestral")
      ? 12
      : v.includes("semestral")
      ? 24
      : 4;

    if (v.includes("2x")) return 2 * semanas;
    if (v.includes("3x")) return 3 * semanas;
    if (v.includes("5x")) return 5 * semanas;
  }

  return 1;
};

const cuentaComoRealizada = (r) =>
  r.estadoSesion === "completada" || r.estadoSesion === "realizada";

function getEstadoPlan(plan) {
  if (!plan) return "activo";

  if (plan.estado_plan === "anulado") {
    return "anulado";
  }

  if (plan.estado_plan === "finalizado") {
    return "finalizado";
  }

  const usadas = Number(plan.sesiones_usadas || 0);
  const totales = Number(plan.sesiones_totales || 0);

  if (totales > 0 && usadas >= totales) {
    return "completado";
  }

  return "activo";
}

function puedeReservarPlan(plan) {
  const estado = getEstadoPlan(plan);

  return (
    estado !== "finalizado" &&
    estado !== "anulado" &&
    estado !== "completado"
  );
}

/* ============================
   Componente Principal
   ============================ */
const Sesiones = () => {
  const [user, setUser] = useState(null);
  const [items, setItems] = useState([]);

  const [filtro, setFiltro] = useState("todas");
  const [busca, setBusca] = useState("");

  const [selectedReserva, setSelectedReserva] = useState(null);
  const [selectedComprobante, setSelectedComprobante] = useState(null);

  const [modalCancel, setModalCancel] = useState(false);
  const [motivoCancel, setMotivoCancel] = useState("");

  const [vista, setVista] = useState("activos");
  const [papeleraUI, setPapeleraUI] = useState({ planes: [], reservas: [] });
  const [menuOpen, setMenuOpen] = useState(null);
  const [waPhone, setWaPhone] = useState("");

useEffect(() => {
  if (!menuOpen) return;
  const close = () => setMenuOpen(null);
  window.addEventListener("click", close);
  return () => window.removeEventListener("click", close);
}, [menuOpen]);


  // Alias para que puedas seguir usando isLoadingPlanes
  const { planes, loadingPlanes: isLoadingPlanes } = usePlanesUsuario(
    user?.uid
  );

  /* ============================
     Auth
     ============================ */
  useEffect(() => {
    return onAuthStateChanged(auth, (u) => setUser(u));
  }, []);

  useEffect(() => {
    const loadWa = async () => {
      try {
        const ref = doc(db, "editar_sitio", "config_global");
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data() || {};
          setWaPhone(data.whatsapp || "");
        }
      } catch (e) {
        console.error("Error cargando whatsapp config_global:", e);
      }
    };
    loadWa();
  }, []);

    /* ============================
     Papelera UI (Firestore) — SOLO UX
     ============================ */
  useEffect(() => {
    if (!user?.uid) return;

    const ref = doc(db, "usuarios", user.uid);

    const unsub = onSnapshot(ref, (snap) => {
      const data = snap.data() || {};
      const p = data.papelera_ui || {};
      setPapeleraUI({
        planes: Array.isArray(p.planes) ? p.planes : [],
        reservas: Array.isArray(p.reservas) ? p.reservas : [],
      });
    });

    return () => unsub();
  }, [user?.uid]);

  useEffect(() => {
  if (vista === "activos") {
    setFiltro("todas"); // en activos no usamos chips de estado
  }
}, [vista]);

  /* ============================
     Cargar reservas
     ============================ */
useEffect(() => {
  if (!user) return;

  // Traer TODAS las reservas del paciente:
  // 1) las que tienen uid = user.uid
  // 2) las que NO tienen uid pero coinciden con el email
  const q = query(
    collection(db, "reservas"),
    where("email", "==", user.email)
  );

  // También necesitamos las reservas normales con uid
  const q2 = query(
    collection(db, "reservas"),
    where("uid", "==", user.uid)
  );

  const unsub1 = onSnapshot(q, (snap) => {
    const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setItems((prev) => {
      const merged = [...prev, ...arr];
      // Eliminar duplicados por ID
      return Array.from(new Map(merged.map((o) => [o.id, o])).values());
    });
  });

  const unsub2 = onSnapshot(q2, (snap) => {
    const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    setItems((prev) => {
      const merged = [...prev, ...arr];
      return Array.from(new Map(merged.map((o) => [o.id, o])).values());
    });
  });

  return () => {
    unsub1();
    unsub2();
  };
}, [user]);


  const planesAgrupados = useMemo(() => {
  const grupos = {};

  items.forEach((r) => {
    const pid = r.planId || "SIN_PLAN";

    if (!grupos[pid]) grupos[pid] = [];
    grupos[pid].push(r);
  });

  return grupos;
}, [items]);


  /* ============================
     Filtrado + búsqueda
     ============================ */
  const filtradas = useMemo(() => {
    return items
      .filter((r) =>
          filtro === "todas" ? true : (r.estado || "").toLowerCase() === filtro
        )
      .filter((r) => {
        if (!busca.trim()) return true;
        const txt = `${r.servicio_categoria} ${r.servicio_variante} ${r.fecha} ${r.hora}`.toLowerCase();
        return txt.includes(busca.toLowerCase());
      });
  }, [items, filtro, busca]);

  /* ============================
     Progreso resumido para la tarjeta
     ============================ */
  const calcularProgreso = (reservaActual) => {
    const plan = planes[reservaActual.planId];
    if (plan) {
      const total = plan.sesiones_totales ?? 1;

      // Para que no dependamos de que el documento del plan esté actualizado,
      // recalculamos a partir de las reservas del mismo plan:
      const reservasDeEstePlan = items.filter(
        (r) => r.planId === reservaActual.planId
      );
      const realizadas = reservasDeEstePlan.filter(cuentaComoRealizada).length;

      const porcentaje =
        total > 0 ? Math.min((realizadas / total) * 100, 100) : 0;

      return {
        total,
        realizadas,
        porcentaje,
      };
    }

    // 2. Lógica antigua (fallback) para reservas sin planId
    const total = sesionesPorServicio(
      reservaActual.servicio_categoria,
      reservaActual.servicio_variante
    );

    const grupo = items.filter(
      (r) =>
        r.servicio_categoria === reservaActual.servicio_categoria &&
        r.servicio_variante === reservaActual.servicio_variante
    );

    const realizadas = grupo.filter(cuentaComoRealizada).length;

    const porcentaje =
      total > 0 ? Math.min((realizadas / total) * 100, 100) : 0;

    return {
      total,
      realizadas,
      porcentaje,
    };
  };

    // ============================
  // Helpers Papelera UI (solo UX)
  // ============================
  const asegurarDocUsuario = async () => {
    const ref = doc(db, "usuarios", user.uid);
    await setDoc(
      ref,
      { papelera_ui: { planes: [], reservas: [] } },
      { merge: true }
    );
    return ref;
  };

  const enviarPlanAPapeleraUI = async (planId) => {
    if (!user?.uid || !planId || planId === "SIN_PLAN") return;
    const ref = await asegurarDocUsuario();
    await updateDoc(ref, { "papelera_ui.planes": arrayUnion(planId) });
  };

  const restaurarPlanDePapeleraUI = async (planId) => {
    if (!user?.uid || !planId || planId === "SIN_PLAN") return;
    const ref = await asegurarDocUsuario();
    await updateDoc(ref, { "papelera_ui.planes": arrayRemove(planId) });
  };

  const enviarReservaAPapeleraUI = async (reservaId) => {
    if (!user?.uid || !reservaId) return;
    const ref = await asegurarDocUsuario();
    await updateDoc(ref, { "papelera_ui.reservas": arrayUnion(reservaId) });
  };

  const restaurarReservaDePapeleraUI = async (reservaId) => {
    if (!user?.uid || !reservaId) return;
    const ref = await asegurarDocUsuario();
    await updateDoc(ref, { "papelera_ui.reservas": arrayRemove(reservaId) });
  };


  /* ============================
     Render
     ============================ */
  if (!user)
    return (
      <div className="max-w-6xl mx-auto px-4 py-10">
        <h1 className="text-2xl font-bold text-blue-900 mb-2">
          Mis Sesiones
        </h1>
        <p className="text-gray-600">
          Inicia sesión para ver tu historial de reservas.
        </p>
      </div>
    );

  return (
    <div className="min-h-screen flex flex-col justify-between bg-gray-50">
      <main className="flex-grow w-full max-w-6xl mx-auto px-4 py-10 relative">
        <div className="w-full max-w-5xl mx-auto">

        {isLoadingPlanes && (
          <div className="mb-4 text-center">
            <span className="inline-block px-3 py-1 bg-blue-50 text-blue-600 text-xs font-semibold rounded-full animate-pulse border border-blue-100">
              🔄 Sincronizando información de tus planes...
            </span>
          </div>
        )}
        {/* Filtros */}
        {/* CONTROLES PRINCIPALES */}
        <div className="mb-4 space-y-3">
          {/* fila 1 — título + vistas */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h1 className="text-2xl font-bold text-blue-900">Mis Sesiones</h1>

            <div className="flex flex-wrap gap-2">
              {[
                { key: "activos", label: "Activos" },
                { key: "historial", label: "Historial" },
                { key: "papelera", label: "Papelera" },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => setVista(t.key)}
                  className={`px-3 py-1.5 rounded-full text-sm border ${
                    vista === t.key
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-blue-900 border-gray-200"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* fila 2 — chips de estado (ocultos si vista=activos) */}
          {vista !== "activos" ? (
            <div className="flex flex-wrap gap-2">
              {estados.map((e) => (
                <button
                  key={e}
                  onClick={() => setFiltro(e.toLowerCase())}
                  className={`px-3 py-1.5 rounded-full text-sm border ${
                    filtro === e.toLowerCase()
                      ? "bg-blue-600 text-white"
                      : "bg-white text-blue-900 border-gray-200"
                  }`}
                >
                  {e.charAt(0).toUpperCase() + e.slice(1)}
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-600">
              Mostrando solo sesiones <strong>pendientes</strong> y{" "}
              <strong>aprobadas</strong>.
            </p>
          )}
        </div>


        {/* Buscador */}
        <div className="mb-4">
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar…"
            className="w-full sm:max-w-xs border rounded-lg px-3 py-2"
          />
        </div>

        {/* Lista */}
        {Object.keys(planesAgrupados).length === 0 ? (
          <div className="bg-white rounded-xl shadow p-6 text-center text-gray-500 mt-10">
            Aún no tienes reservas registradas.
          </div>
        ) : (
          Object.entries(planesAgrupados).map(([planId, reservas]) => {
            const plan = planes[planId];


        if (!plan) {
          const reservasFiltradas = reservas
            // 1) Vista (activos/todas/papelera) — SOLO UX
            .filter((r) => {
              const enPapelera = papeleraUI.reservas.includes(r.id);

              if (vista === "papelera") return enPapelera;
              if (vista === "historial") return !enPapelera;

              // activos: solo pendiente/aprobada y NO en papelera
              return (
                !enPapelera &&
                ["pendiente", "aprobada"].includes((r.estado || "").toLowerCase())
              );
            })
            // 2) Filtro por estado (chips)
            .filter((r) =>
              filtro === "todas"
                ? true
                : (r.estado || "").toLowerCase() === filtro
            )
            // 3) Búsqueda
            .filter((r) => {
              if (!busca.trim()) return true;
              const txt =
                `${r.servicio_categoria} ${r.servicio_variante} ${r.fecha} ${r.hora}`.toLowerCase();
              return txt.includes(busca.toLowerCase());
            });

          return reservasFiltradas.map((r) => {
            const enPapelera = papeleraUI.reservas.includes(r.id);

        return (
          <div
            key={r.id}
            className="bg-white rounded-xl shadow border p-4 mb-4 cursor-pointer hover:shadow-lg transition relative"
            onClick={() => setSelectedReserva(r)}
          >
            {/* ⋮ Menú */}
            <button
              className="absolute top-3 right-3 p-2 rounded-lg hover:bg-gray-100"
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen((prev) =>
                  prev?.type === "reserva" && prev?.id === r.id
                    ? null
                    : { type: "reserva", id: r.id }
                );
              }}
              aria-label="Opciones"
            >
              <MoreVertical className="w-5 h-5 text-gray-600" />
            </button>

            {menuOpen?.type === "reserva" && menuOpen?.id === r.id && (
              <div
                className="absolute top-12 right-3 bg-white border rounded-xl shadow-lg overflow-hidden z-50 min-w-[190px]"
                onClick={(e) => e.stopPropagation()}
              >
                {enPapelera ? (
                  <button
                    className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm"
                    onClick={async () => {
                      await restaurarReservaDePapeleraUI(r.id);
                      setMenuOpen(null);
                    }}
                  >
                    Restaurar
                  </button>
                ) : (
                  <button
                    className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm"
                    onClick={async () => {
                      await enviarReservaAPapeleraUI(r.id);
                      setMenuOpen(null);
                    }}
                  >
                    Enviar a papelera
                  </button>
                )}
              </div>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 pr-12">
              <h3 className="font-semibold text-blue-900">
                {r.servicio_categoria}: {r.servicio_variante}
              </h3>
              <Pill estado={r.estado} />
            </div>

            <p className="text-sm text-gray-600 mt-1">
              Fecha: {r.fecha} — Hora: {r.hora}
            </p>

            <p className="text-sm text-gray-600 mt-1">
              {r.pago_tipo === "incluida" && "Sesión incluida en plan activo"}
              {r.pago_tipo === "completo" &&
                `Pago completo: ${clp(r.monto_pagado)}`}
              {r.pago_tipo === "reserva50" &&
                `Pago inicial: ${clp(r.monto_pagado)}`}
              {r.pago_tipo === "pago_proxima" &&
                `Pago sesión: ${clp(r.monto_pagado)}`}
              {r.pago_tipo === "pago_total_restante" &&
                `Pago saldo: ${clp(r.monto_pagado)}`}
            </p>

            {["rechazada", "cancelada"].includes((r?.estado || "").toLowerCase()) && (
              <div className="mt-3 rounded-xl border border-zinc-200 bg-zinc-50 p-3 text-sm">
                {(() => {
                  const info = motivoReservaTexto(r);
                  if (!info.titulo) return null;

                  const waText = [
                    `Hola 👋, tengo una consulta sobre mi reserva en EKINE.`,
                    `Fecha: ${r?.fecha || "—"} ${r?.hora ? `(${r.hora})` : ""}`,
                    `Estado: ${r?.estado || "—"}`,
                    ...info.lineas,
                  ].join("\n");

                  const waLink = buildWaLink(waPhone, waText);

                  return (
                    <div className="space-y-2">
                      <div className="font-semibold text-zinc-800">{info.titulo}</div>

                      <ul className="list-disc pl-5 text-zinc-700 space-y-1">
                        {info.lineas.map((t, i) => (
                          <li key={i}>{t}</li>
                        ))}
                      </ul>

                      {waLink && (
                        <a
                          href={waLink}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Consultar por WhatsApp
                        </a>
                      )}

                      {!waLink && (
                        <div className="text-xs text-zinc-500">
                          WhatsApp no configurado en <code>editar_sitio/config_global</code>.
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        );
      });
    }



    // SI SÍ EXISTE PLAN → tarjeta única

    // ============================
    // 1) CÁLCULOS — SIEMPRE CON TODAS LAS RESERVAS DEL PLAN (sin filtro)
    // ============================
    const reservasDelPlan = reservas;
    const planEnPapelera = papeleraUI.planes.includes(planId);

    const reservasValidasCupo = reservasDelPlan.filter((r) =>
      ["pendiente", "aprobada", "realizada"].includes((r.estado || "").toLowerCase())
    );

      const estadoPlan = getEstadoPlan(plan);

      const completadoPlan =
        estadoPlan === "completado";

    // Tabs SOLO UX
    if (vista === "papelera" && !planEnPapelera) return null;
    if (vista === "historial" && planEnPapelera) return null;
    if (
      vista === "activos" &&
      (
        planEnPapelera ||
        estadoPlan === "completado" ||
        estadoPlan === "finalizado" ||
        estadoPlan === "anulado"
      )
    ) {
      return null;
    }


    const sesionesValidasTotales = reservasDelPlan.filter(r =>
      ["pendiente", "aprobada", "realizada"].includes(r.estado)
    );

    const realizadas = sesionesValidasTotales.filter(cuentaComoRealizada).length;

    const reservadas = sesionesValidasTotales.filter(r =>
      ["pendiente", "aprobada"].includes(r.estado)
    ).length;

    const total = plan.sesiones_totales;

    const pendientes = Math.max(total - realizadas, 0);

    const progresoPct =
      total > 0 ? Math.min((realizadas / total) * 100, 100) : 0;


    // ============================
    // 2) LISTA FILTRADA — SOLO PARA MOSTRAR
    // ============================
    const reservasFiltradas = reservasDelPlan
      .filter(r => filtro === "todas" ? true : r.estado.toLowerCase() === filtro)
      .filter(r => {
        if (!busca.trim()) return true;
        const txt = `${r.fecha} ${r.hora} ${r.estado}`.toLowerCase();
        return txt.includes(busca.toLowerCase());
      })
      .sort((a, b) => (a.fecha > b.fecha ? 1 : -1));


    // ============================
    // RENDER DE LA TARJETA DEL PLAN
    // ============================
    return (
      <div
        key={planId}
        className="bg-white rounded-xl shadow border p-5 mb-6 transition w-full overflow-hidden"
      >
      <div className="flex items-center justify-between mb-2 relative">
        <h3 className="font-semibold text-blue-900 text-lg pr-10">
          {plan.categoria}: {plan.variante}
        </h3>

        <button
          className="p-2 rounded-lg hover:bg-gray-100"
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((prev) =>
              prev?.type === "plan" && prev?.id === planId
                ? null
                : { type: "plan", id: planId }
            );
          }}
          aria-label="Opciones"
        >
          <MoreVertical className="w-5 h-5 text-gray-600" />
        </button>

        {menuOpen?.type === "plan" && menuOpen?.id === planId && (
          <div
            className="absolute top-10 right-0 bg-white border rounded-xl shadow-lg overflow-hidden z-50 min-w-[190px]"
            onClick={(e) => e.stopPropagation()}
          >
            {planEnPapelera ? (
              <button
                className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm"
                onClick={async () => {
                  await restaurarPlanDePapeleraUI(planId);
                  setMenuOpen(null);
                }}
              >
                Restaurar
              </button>
            ) : (
              <button
                className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm"
                onClick={async () => {
                  await enviarPlanAPapeleraUI(planId);
                  setMenuOpen(null);
                }}
              >
                Enviar a papelera
              </button>
            )}
          </div>
        )}
      </div>

      {estadoPlan === "finalizado" && (
        <div className="mb-4 bg-amber-50 border border-amber-200 text-amber-800 rounded-2xl p-4">

          <div className="font-semibold">
            Este plan fue finalizado por administración.
          </div>

          {plan.motivo_cierre && (
            <div className="text-sm mt-1">
              <strong>Motivo:</strong> {plan.motivo_cierre}
            </div>
          )}
        </div>
      )}

      {estadoPlan === "anulado" && (
        <div className="mb-4 bg-rose-50 border border-rose-200 text-rose-800 rounded-2xl p-4">

          <div className="font-semibold">
            Este plan fue anulado por administración.
          </div>

          {plan.motivo_anulacion && (
            <div className="text-sm mt-1">
              <strong>Motivo:</strong> {plan.motivo_anulacion}
            </div>
          )}
        </div>
      )}

        {/* PROGRESO — SIEMPRE SIN FILTRO */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4 text-sm text-gray-700">
          <p>Realizadas: {realizadas} / {total}</p>
          <p>Reservadas: {reservadas}</p>
          <p>Pendientes: {pendientes}</p>
        </div>

        <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
          <div
            className="h-2 rounded-full bg-blue-600"
            style={{ width: `${progresoPct}%` }}
          />
        </div>

        {/* LISTA DE SESIONES FILTRADAS */}
        <div className="mt-4">
          {reservasFiltradas.map((r) => (
            <div
              key={r.id}
              onClick={() => setSelectedReserva(r)}
              className="border rounded-lg p-3 mb-2 cursor-pointer hover:bg-gray-50 transition"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                <span className="font-medium text-blue-900">
                  {r.fecha} — {r.hora}
                </span>
                <Pill estado={r.estado} />
              </div>

              <p className="text-xs text-gray-600">
                Pago: {clp(r.monto_pagado)}
              </p>
            </div>
          ))}
        </div>

        {/* BOTÓN — Reservar siguiente sesión */}
        {(() => {
          // Sesiones que ocupan un cupo real del plan
          const reservasValidas = reservasDelPlan.filter(r =>
            ["pendiente", "aprobada", "realizada"].includes(r.estado)
          );

          const ocupadasValidas = reservasValidas.length;

          // SI YA TIENEN TODAS LAS QUE CORRESPONDEN → OCULTAR
          if (
            ocupadasValidas >= total ||
            !puedeReservarPlan(plan)
          ) {
            return null;
          }

          // Identificar la última sesión válida
          const reservasOrdenadas = reservasValidas.sort((a, b) =>
            a.fecha > b.fecha ? 1 : -1
          );

          const ultima = reservasOrdenadas[reservasOrdenadas.length - 1];

          return (
            <button
              onClick={() => {
                // Nombre EXACTO del servicio tal como aparece en agenda.jsx
                const servicioLabel = plan.servicio || plan.categoria;

                localStorage.setItem("servicioSeleccionado", JSON.stringify({
                  // Información del servicio para preseleccionar en agenda.jsx
                  categoria: servicioLabel,
                  variante: plan.servicio_variante || plan.variante,

                  // Información completa del plan
                  planId,
                  sesiones_totales: plan.sesiones_totales,
                  sesiones_usadas: plan.sesiones_usadas,
                  sesiones_reservadas: plan.sesiones_reservadas,
                  precio_total: plan.precio_total,
                  precio_por_sesion: plan.precio_por_sesion,
                  monto_restante: plan.monto_restante,
                  pago_total_realizado: plan.pago_total_realizado,
                }));


                localStorage.setItem("flujoReserva", "plan");
                localStorage.setItem("reservasPlan", JSON.stringify(reservasValidas));

                window.location.href = "/agenda";
              }}
              className="mt-4 w-full py-3 text-center rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition text-sm sm:text-base"
            >
              Reservar siguiente sesión
            </button>
          );
        })()}
      </div>
    );

  })
)}

        {/* ============================
            MODAL DETALLE DE RESERVA
           ============================ */}
        {selectedReserva && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
            onClick={() => setSelectedReserva(null)}
          >
            <div
              className="bg-white/70 backdrop-blur-2xl border border-white/40 rounded-2xl shadow-2xl w-[95%] sm:max-w-lg mx-auto p-4 sm:p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-2xl font-bold text-blue-900 mb-3">
                {selectedReserva.servicio_categoria}:{" "}
                {selectedReserva.servicio_variante}
              </h2>

              <p className="text-sm text-gray-700 mb-1">
                <strong>Fecha:</strong> {selectedReserva.fecha} —{" "}
                <strong>Hora:</strong> {selectedReserva.hora}
              </p>

              <p className="text-sm text-gray-700 mb-3 flex items-center gap-1">
                <strong>Estado:</strong>{" "}
                <Pill estado={selectedReserva.estado} />
              </p>

              {["rechazada", "cancelada"].includes((selectedReserva?.estado || "").toLowerCase()) && (
                <div className="mt-3 rounded-xl border border-zinc-200 bg-zinc-50 p-3 text-sm">
                  {(() => {
                    const info = motivoReservaTexto(selectedReserva);
                    if (!info.titulo) return null;

                    const waText = [
                      `Hola 👋, tengo una consulta sobre mi reserva en EKINE.`,
                      `Fecha: ${selectedReserva?.fecha || "—"} ${selectedReserva?.hora ? `(${selectedReserva.hora})` : ""}`,
                      `Estado: ${selectedReserva?.estado || "—"}`,
                      ...info.lineas,
                    ].join("\n");

                    const waLink = buildWaLink(waPhone, waText);

                    return (
                      <div className="space-y-2">
                        <div className="font-semibold text-zinc-800">{info.titulo}</div>

                        <ul className="list-disc pl-5 text-zinc-700 space-y-1">
                          {info.lineas.map((t, i) => (
                            <li key={i}>{t}</li>
                          ))}
                        </ul>

                        {waLink && (
                          <a
                            href={waLink}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Consultar por WhatsApp
                          </a>
                        )}

                        {!waLink && (
                          <div className="text-xs text-zinc-500">
                            WhatsApp no configurado en <code>editar_sitio/config_global</code>.
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
              {/* ============================
                  BLOQUE PLAN REFACTORIZADO
                 ============================ */}
              {(() => {
                const planAsociado =
                  planes[selectedReserva.planId] ||
                  Object.values(planes).find(
                    (p) =>
                      p.categoria === selectedReserva.servicio_categoria &&
                      p.variante === selectedReserva.servicio_variante
                  );

                const totalSesiones =
                  planAsociado?.sesiones_totales ||
                  sesionesPorServicio(
                    selectedReserva.servicio_categoria,
                    selectedReserva.servicio_variante
                  );

                if (totalSesiones <= 1 || !planAsociado) return null;

                const {
                  planId,
                  categoria,
                  variante,
                  sesiones_totales,
                  sesiones_usadas,
                  sesiones_reservadas,
                  precio_total,
                  precio_por_sesion,
                  monto_restante,
                } = planAsociado;

                // ---- Cálculos a partir de las reservas reales del plan ----
                const reservasDeEstePlan = items.filter(
                  (r) => r.planId === planId
                );

                const realizadasCount =
                  reservasDeEstePlan.filter(cuentaComoRealizada).length;

                const reservadasTotal = reservasDeEstePlan.length; // incluye realizadas

                const pendientes = Math.max(
                  sesiones_totales - realizadasCount,
                  0
                );

                const correlativa = Math.min(
                  reservadasTotal + 1,
                  sesiones_totales
                );

                const disponibles = Math.max(
                  sesiones_totales - reservadasTotal,
                  0
                );

                const completado = realizadasCount >= sesiones_totales;

                const progresoPct =
                  sesiones_totales > 0
                    ? Math.min(
                        (realizadasCount / sesiones_totales) * 100,
                        100
                      )
                    : 0;

                // ============================
                // Lógica botón reservar siguiente
                // ============================
                const reservasMismoPlan = reservasDeEstePlan.sort((a, b) =>
                  a.fecha > b.fecha ? 1 : -1
                );
                const ultimaReserva =
                  reservasMismoPlan[reservasMismoPlan.length - 1];

                const esActualLaUltima =
                  ultimaReserva?.id === selectedReserva.id;

                const puedeReservarSiguiente =
                  disponibles > 0 &&
                  !completado &&
                  esActualLaUltima &&
                  ["aprobada", "realizada", "pendiente"].includes(
                    selectedReserva.estado
                  );

                return (
                  <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-4">
                    <h3 className="text-blue-900 font-semibold text-lg mb-2">
                      Plan asociado
                    </h3>

                    {/* Datos económicos */}
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm mb-3">
                      <p>
                        <strong>Precio plan:</strong> {clp(precio_total)}
                      </p>
                      <p>
                        <strong>Valor sesión:</strong>{" "}
                        {clp(precio_por_sesion)}
                      </p>
                      <p>
                        <strong>Saldo:</strong>{" "}
                        <span
                          className={
                            monto_restante > 0
                              ? "text-rose-700 font-bold"
                              : "text-emerald-700 font-bold"
                          }
                        >
                          {clp(monto_restante)}
                        </span>
                      </p>
                      <p>
                        <strong>Pagado:</strong>{" "}
                        <span className="font-semibold text-blue-800">
                          {clp(planAsociado?.pago_total_realizado || 0)}
                        </span>
                      </p>

                      <p>
                        <strong>Estado pago:</strong>{" "}
                        {monto_restante === 0
                          ? "Pago completo del plan"
                          : planAsociado?.pago_total_realizado > 0
                            ? "Pago inicial registrado"
                            : "Pago pendiente"}
                      </p>
                    </div>

                    {/* Resumen tipo C (profesional) */}
                    <div className="mt-1 text-sm text-gray-800 space-y-1">
                      <p>
                        <strong>Sesiones totales:</strong> {sesiones_totales}
                      </p>
                      <p>
                        <strong>Realizadas:</strong> {realizadasCount}
                      </p>
                      <p>
                        <strong>Reservadas:</strong>{" "}
                        {reservadasTotal}
                      </p>
                      <p>
                        <strong>Próxima sesión:</strong>{" "}
                        {completado
                          ? "Plan completado"
                          : `${correlativa} de ${sesiones_totales}`}
                      </p>
                      <p>
                        <strong>Pendientes:</strong> {pendientes}
                      </p>
                    </div>

                    <div className="mt-3">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="h-2 rounded-full bg-blue-600"
                          style={{ width: `${progresoPct}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-600 mt-1">
                        Progreso: {realizadasCount} / {sesiones_totales}
                      </p>
                    </div>

                    {completado && (
                      <p className="mt-2 text-xs text-emerald-700 font-semibold">
                        ✅ Plan completado.
                      </p>
                    )}

                    {/* BOTÓN RESERVAR SIGUIENTE SESIÓN */}

                  </div>
                );
              })()}

              {/* ============================
                  BLOQUE PAGO
                 ============================ */}
              <p className="text-sm text-gray-700 mb-1">
                <strong>Pago:</strong>{" "}
                {selectedReserva.pago_tipo === "incluida" &&
                  "Sesión incluida en plan activo"}
                {selectedReserva.pago_tipo === "completo" &&
                  "Pago completo realizado"}
                {selectedReserva.pago_tipo === "reserva50" &&
                  "Pago inicial registrado"}
                {selectedReserva.pago_tipo === "pago_proxima" &&
                  "Pago de próxima sesión"}
                {selectedReserva.pago_tipo === "pago_total_restante" &&
                  "Pago del saldo total del plan"}
              </p>

              <p className="text-sm text-gray-700 mb-4">
                <strong>Monto pagado:</strong>{" "}
                {clp(selectedReserva.monto_pagado)}
              </p>

              {/* ============================
                  COMPROBANTES
                 ============================ */}
              {selectedReserva.comprobantes?.length > 0 ? (
                <div className="mt-3">
                  <p className="text-sm font-semibold text-gray-700 mb-1">
                    Comprobantes:
                  </p>
                  {selectedReserva.comprobantes.map((c, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedComprobante(c.url)}
                      className="block text-blue-700 underline text-sm hover:text-blue-900 mb-1"
                    >
                      Ver comprobante {i + 1}
                    </button>
                  ))}
                </div>
              ) : selectedReserva.comprobanteURL ? (
                <button
                  onClick={() =>
                    setSelectedComprobante(selectedReserva.comprobanteURL)
                  }
                  className="text-blue-700 underline text-sm font-medium"
                >
                  Ver comprobante
                </button>
              ) : (
                <p className="text-sm text-gray-500">
                  No hay comprobantes disponibles.
                </p>
              )}

              {/* ============================
                  BOTÓN CANCELAR SESIÓN
                 ============================ */}
              {["pendiente", "aprobada"].includes(selectedReserva.estado) &&
                !esPasada(selectedReserva) && (
                  <div className="mt-6 border-t border-gray-200 pt-4">
                    <p className="text-sm font-semibold text-gray-700 mb-2">
                      ¿No podrás asistir?
                    </p>

                    <button
                      onClick={() => {
                        setModalCancel(true);
                        setMotivoCancel("");
                      }}
                      className="w-full px-4 py-2 rounded-lg bg-rose-100 hover:bg-rose-200 text-rose-800 font-medium transition"
                    >
                      Solicitar cancelación de sesión
                    </button>
                  </div>
                )}

              <button
                onClick={() => setSelectedReserva(null)}
                className="absolute top-3 right-4 text-gray-600 hover:text-gray-800 text-lg"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* ============================
            MODAL COMPROBANTE
           ============================ */}
        {selectedComprobante && (
          <div
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center z-[60]"
            onClick={() => setSelectedComprobante(null)}
          >
            <div
              className="relative w-[95%] sm:max-w-3xl max-h-[85vh] overflow-auto p-3 sm:p-4 rounded-2xl bg-white/10 backdrop-blur-md shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedComprobante}
                alt="Comprobante"
                className="rounded-xl max-h-[80vh] mx-auto object-contain cursor-zoom-in"
              />
              <div className="flex justify-end gap-3 mt-4">
                <button
                  onClick={() => setSelectedComprobante(null)}
                  className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition"
                >
                  Volver
                </button>
                <button
                  onClick={() => {
                    setSelectedComprobante(null);
                    setSelectedReserva(null);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ============================
            SUBMODAL CANCELACIÓN
           ============================ */}
        {modalCancel && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]"
            onClick={() => setModalCancel(false)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 relative"
            >
              <h3 className="text-xl font-semibold text-blue-900 mb-3">
                Solicitud de cancelación
              </h3>

              <label className="block text-sm text-gray-700 mb-1">
                Motivo de la cancelación
              </label>
              <textarea
                value={motivoCancel}
                onChange={(e) => setMotivoCancel(e.target.value)}
                className="border rounded-lg w-full p-2 mb-3 h-24"
                placeholder="Describe brevemente el motivo…"
              />

              <p className="text-xs text-gray-500 mb-3">
                Si corresponde reembolso, deberás coordinarlo directamente con
                la kinesióloga.
              </p>

              <div className="flex justify-end gap-3 mt-2">
                <button
                  onClick={() => setModalCancel(false)}
                  className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition"
                >
                  Cancelar
                </button>

                <button
                  onClick={async () => {
                    await addDoc(collection(db, "solicitudes_modificacion"), {
                      tipo: "cancelacion",
                      reservaId: selectedReserva.id,
                      uid: user.uid,
                      email: user.email,
                      nombre_usuario:
                        user.displayName || user.email || "Usuario",
                      motivo: motivoCancel || "No especificado",
                      fecha_solicitud: new Date(),
                      estado: "pendiente",
                    });

                    alert(
                      "Tu solicitud de cancelación ha sido enviada. Recibirás una copia en tu correo."
                    );
                    setModalCancel(false);
                  }}
                  className="px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition"
                >
                  Enviar solicitud
                </button>
              </div>

              <button
                onClick={() => setModalCancel(false)}
                className="absolute top-3 right-4 text-gray-600 hover:text-gray-800 text-lg"
              >
                ✕
              </button>
            </div>
          </div>
        )}
      </div>
  
      </main>
      
    </div>
  );
};

export default Sesiones;
